/**
 * EditContributionModalV2
 *
 * Modal for employees to configure their pension contribution amount.
 * Design matches Lovable reference (ConfigureContributionDialog).
 *
 * Handles:
 * - Fixed amount or percentage-based contributions
 * - Legal contribution limits from calculateMaxAportationsInfo API
 * - 4-day lockout before contribution day
 * - Debounced updates for slider
 * - Indexa Capital integration
 * - Individual pension plan flow
 */
import React, { useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
	Info,
	AlertTriangle,
	CheckCircle2,
	Sparkles,
	Building2,
	User,
	TrendingUp,
	Loader2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Slider } from '../ui/slider';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import ResponsiveTooltip from '../ui/responsive-tooltip';

import { ArcaContext } from '../../context/ArcaContext.context';
import { ContributionTypeEnum } from '../../api/users/interfaces/ContributionData';
import { ContributionPeriodicity, EmployeeContributionTypeEnum } from '../../types/enums';
import {
	MatchingBracketDataI,
	SeniorityBracketData
} from '../../api/companies/interfaces/CompanyEppConfig';
import {
	resolveMatchingContribution,
	resolveMatchingTier
} from '../../utils/resolveMatchingContribution';
import calculateMaxAportationsInfo, {
	MaxAportationsInfoOutput
} from '../../api/companies/calculateMaxAportationsInfo';
import { fmtNum, fmtEur } from '../../utils/formatArcaNumbers';
import { calculateContributionBreakdown } from '../../utils/contributionBreakdown';
import { CONTRIBUTION_LIMITS } from '../../utils/contributionLimits';
import { calculateMinEmployeeContribution } from '../../utils/calculateMinEmployeeContribution';
import {
	percentageToMonthlyEur as percentageToMonthlyEurUtil,
	snapPercentageToGrid,
	floorPercentageToGrid,
	ceilPercentageToGrid,
	CONTRIBUTION_PERCENTAGE_STEP
} from '../../utils/contributionPercentage';
import { analytics } from '../../services/analytics';
import { CONTRIBUTION_ADJUSTMENT_METHODS } from '../../constants/contributionAdjustmentMethods';
import { shouldFireContributionUpdated } from '../../utils/shouldFireContributionUpdated';

import IndividualPensionPlanFlowV2 from './IndividualPensionPlanFlowV2';

// Helper: calculate years of service from employment start date
const calculateYearsOfService = (employmentStartDate: string): number => {
	const start = new Date(employmentStartDate);
	const now = new Date();
	const diffYears = (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
	return Math.floor(diffYears);
};

export interface EditContributionModalV2Props {
	isOpen: boolean;
	onClose: () => void;
	contributionType: ContributionTypeEnum;
	setContributionType: (value: ContributionTypeEnum) => void;
	contributionFixedAmount: number | '' | undefined;
	handleChangeFixedContribution: (value: number) => void;
	/** Si devuelve `false`, no se guardó (y no se registra `contribution_updated`). */
	handleSaveChangesEditFixedContribution: () => void | boolean | Promise<void | boolean>;
	translate: (key: string) => string;
	companyEppConfig?: {
		contributionDay: number;
		employeeContributionRequired: boolean;
		employeeContributionMinimum: number;
		contributionPeriodicity: ContributionPeriodicity;
		seniorityBracket?: SeniorityBracketData | null;
		matchingBracket?: MatchingBracketDataI | null;
	};
	/** Employee category (DIRECTOR/NON_DIRECTOR/...) — used to resolve the MATCHING tier */
	employeeCategory?: string;
	contributeAlwaysMax: boolean;
	setContributeAlwaysMax: React.Dispatch<React.SetStateAction<boolean>>;
	userGrossAnnualSalary?: number;
	employeeContributionType?: ContributionTypeEnum;
	resolvedMonthlyCompanyContribution?: number;
	isIndividual?: boolean;
	/**
	 * Señal de CAP fiscal: `true` para toda persona autónoma (societaria o pura)
	 * → cap reducido 4.250/5.750. Un societario opera su plan de EMPLEO pero
	 * conserva el cap 5.750. Si se omite, cae a `false` (cap de empleado).
	 */
	usesReducedCap?: boolean;
	/**
	 * Euros que suma el enlace del correo (`?action=increase_contribution&amount=50`,
	 * Arca-Team/product#3). Con él el modal arranca en `current + amount` (con tope en el
	 * máximo y nunca por debajo del mínimo de la empresa) y no deja bajar de lo que ya aporta.
	 * Sin él, el modal es el de siempre.
	 */
	presetDeltaAmount?: number;
	/**
	 * Lo abrió el enlace del correo (chirigota#630). Va aparte de `presetDeltaAmount` porque el
	 * wrapper entrega el importe cuando ha cargado la config, y la apertura se registra antes.
	 */
	openedFromEmail?: boolean;
}

const EditContributionModalV2: React.FC<EditContributionModalV2Props> = ({
	isOpen,
	onClose,
	contributionFixedAmount,
	handleChangeFixedContribution,
	handleSaveChangesEditFixedContribution,
	translate,
	companyEppConfig,
	employeeCategory,
	contributeAlwaysMax,
	setContributeAlwaysMax,
	userGrossAnnualSalary = 0,
	employeeContributionType,
	resolvedMonthlyCompanyContribution,
	isIndividual = false,
	usesReducedCap: usesReducedCapProp,
	presetDeltaAmount,
	openedFromEmail = false
}) => {
	const { token } = useContext(ArcaContext);

	// Cap reducido de autónomo (4.250 / 5.750). Un societario opera su plan de
	// EMPLEO pero conserva el cap reducido. Si no se pasa → cap de empleado.
	const usesReducedCap = usesReducedCapProp ?? false;

	// State
	const [maxAportationsInfo, setMaxAportationsInfo] = useState<MaxAportationsInfoOutput>();
	const [monthlyAmount, setMonthlyAmount] = useState<number>(
		typeof contributionFixedAmount === 'number' ? contributionFixedAmount : 0
	);
	const [percentageValue, setPercentageValue] = useState<number>(0);
	const [isSaving, setIsSaving] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	// Flow state
	const [showPensionPlanFlow, setShowPensionPlanFlow] = useState(false);
	const [shouldEnableAlwaysMax, setShouldEnableAlwaysMax] = useState(false);

	// Preset del enlace del correo: lo que aportaba al abrir (el suelo) y el importe con el que
	// arrancó, para saber al guardar si se quedó con él o lo cambió. `null` sin preset.
	const [presetBaseAmount, setPresetBaseAmount] = useState<number | null>(null);
	const presetInitialAmountRef = useRef<number | null>(null);
	const presetAppliedRef = useRef(false);

	// Debounce ref
	const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Fetch max aportations info
	const fetchMaxAportationsInfo = useCallback(async (): Promise<{
		employeeData?: MaxAportationsInfoOutput;
	}> => {
		setIsLoading(true);
		try {
			// Fase 4 multi-cuenta (fix 21-jul): mode explícito para que un usuario
			// con ≥2 filas reciba el límite/aportación del plan que este modal
			// está editando, no "el" plan por defecto.
			const res = await calculateMaxAportationsInfo(token, 'EMPLOYMENT');
			const employeeData = res.data as MaxAportationsInfoOutput;
			setMaxAportationsInfo(employeeData);
			const current = employeeData.currentConfig?.participantContributionPerPeriod ?? 0;
			setMonthlyAmount(current);
			handleChangeFixedContribution(current);
			return { employeeData };
		} catch (error) {
			console.error('Error fetching max aportations info:', error);
			return {};
		} finally {
			setIsLoading(false);
		}
	}, [token, handleChangeFixedContribution]);

	// Fetch limits when modal opens
	useEffect(() => {
		if (isOpen) {
			fetchMaxAportationsInfo();
		}
	}, [isOpen, fetchMaxAportationsInfo]);

	// Track contribution_modal_opened solo en la transición false→true (no en re-renders).
	// initialContributionAmountRef captura el importe que el usuario tenía configurado
	// ANTES de abrir el modal, para diferenciar modificación vs primera configuración.
	// Si se lee al click "Confirmar", el slider y el botón "máximo" ya habrán actualizado
	// el prop del padre vía handleChangeFixedContribution, dando un falso negativo.
	// Captura el PRIMER valor numérico disponible mientras el modal está abierto
	// (el padre puede pasar `undefined` en el primer render y cargar async después),
	// y reseteamos al cerrar.
	const wasOpenRef = useRef(false);
	const initialContributionAmountRef = useRef<number | null>(null);
	useEffect(() => {
		if (isOpen) {
			if (!wasOpenRef.current) {
				analytics.contributionModalOpened({
					sourcePage: window.location.pathname,
					fromEmail: openedFromEmail || Boolean(presetDeltaAmount)
				});
			}
			// Capturamos el primer valor numérico > 0. El wrapper inicializa
			// el state en 0 antes del fetch async, así que tratamos 0 igual
			// que null (= aún no cargado). Si la aportación previa real era
			// 0 (1ª config), el helper shouldFireContributionUpdated rechaza
			// igualmente.
			if (
				(initialContributionAmountRef.current === null ||
					initialContributionAmountRef.current === 0) &&
				typeof contributionFixedAmount === 'number' &&
				contributionFixedAmount > 0
			) {
				initialContributionAmountRef.current = contributionFixedAmount;
			}
		} else {
			initialContributionAmountRef.current = null;
		}
		wasOpenRef.current = isOpen;
	}, [isOpen, contributionFixedAmount]);

	// Cleanup debounce
	useEffect(() => {
		return () => {
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}
		};
	}, []);

	// Calculate days until contribution
	const { contributionDay, daysUntilContribution } = useMemo(() => {
		const today = new Date();
		const day = companyEppConfig?.contributionDay ?? 1;
		let daysUntil = day - today.getDate();

		if (daysUntil < 0) {
			const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
			daysUntil = lastDayOfMonth - today.getDate() + day;
		}

		return { contributionDay: day, daysUntilContribution: daysUntil };
	}, [companyEppConfig?.contributionDay]);

	// Calculated values
	const companyAnnualContribution = useMemo(() => {
		if (maxAportationsInfo?.totalCompanyContributions) {
			return maxAportationsInfo.totalCompanyContributions;
		}
		return 0;
	}, [maxAportationsInfo]);

	const companyCommittedAnnual = useMemo(() => {
		return maxAportationsInfo?.companyThisYearCompanyCommitted || companyAnnualContribution;
	}, [maxAportationsInfo, companyAnnualContribution]);

	const maxMonthlyEmployee = useMemo(() => {
		return maxAportationsInfo?.maxUserContributionEndOfNextPeriod || 0;
	}, [maxAportationsInfo]);

	const minMonthlyEmployee = useMemo(
		() =>
			calculateMinEmployeeContribution({
				employeeContributionRequired: companyEppConfig?.employeeContributionRequired,
				employeeContributionMinimum: companyEppConfig?.employeeContributionMinimum,
				employeeContributionType: employeeContributionType as string,
				grossAnnualSalary: userGrossAnnualSalary
			}),
		[companyEppConfig, employeeContributionType, userGrossAnnualSalary]
	);

	// Con el preset, cuando llegan los límites: `current + amount`, con tope en el máximo (si se
	// conoce) y nunca por debajo del mínimo de la empresa. UNA vez por apertura: los límites se
	// vuelven a pedir (p. ej. al activar «siempre el máximo») y no deben reponerlo. No se aplica
	// si la empresa aporta por porcentaje (el importe sería un %), ni si ya está en el máximo.
	useEffect(() => {
		if (!isOpen) {
			presetAppliedRef.current = false;
			return;
		}
		if (!presetDeltaAmount || !maxAportationsInfo || presetAppliedRef.current) return;
		presetAppliedRef.current = true;
		const current = maxAportationsInfo.currentConfig?.participantContributionPerPeriod ?? 0;
		if (isPercentageMode || (maxMonthlyEmployee > 0 && maxMonthlyEmployee <= current)) return;
		const raised = current + presetDeltaAmount;
		const capped = maxMonthlyEmployee > 0 ? Math.min(raised, maxMonthlyEmployee) : raised;
		const initial = Math.max(capped, minMonthlyEmployee);
		presetInitialAmountRef.current = initial;
		setPresetBaseAmount(current);
		setMonthlyAmount(initial);
		handleChangeFixedContribution(initial);
		// Un importe concreto: como al mover el slider, deja de ser «siempre el máximo».
		setContributeAlwaysMax(false);
	}, [isOpen, presetDeltaAmount, maxAportationsInfo]); // eslint-disable-line react-hooks/exhaustive-deps

	// Suelo del slider: el mínimo de la empresa y, solo con el preset, lo que ya aporta.
	const sliderMinMonthly =
		presetBaseAmount !== null ? Math.max(minMonthlyEmployee, presetBaseAmount) : minMonthlyEmployee;

	// Clamp slider value when min changes (e.g. after data loads)
	useEffect(() => {
		if (minMonthlyEmployee > 0 && monthlyAmount < minMonthlyEmployee) {
			setMonthlyAmount(minMonthlyEmployee);
			handleChangeFixedContribution(minMonthlyEmployee);
		}
	}, [minMonthlyEmployee]); // eslint-disable-line react-hooks/exhaustive-deps

	const alreadyContributedEmployee = useMemo(() => {
		return maxAportationsInfo?.totalParticipantContributions || 0;
	}, [maxAportationsInfo]);

	const externalPlanContributions = maxAportationsInfo?.individualExtraAportations || 0;

	const breakdown = useMemo(() => {
		if (!maxAportationsInfo) return null;
		return calculateContributionBreakdown({
			companyAnnualCommitted: companyCommittedAnnual,
			totalCompanyContributions: maxAportationsInfo.totalCompanyContributions || 0,
			totalParticipantContributions: maxAportationsInfo.totalParticipantContributions || 0,
			individualExtraAportations: externalPlanContributions,
			recommendedMonthlyContribution:
				maxAportationsInfo.recommendedConfig?.participantContributionPerPeriod || 0,
			currentMonth: new Date().getMonth() + 1,
			currentDay: new Date().getDate(),
			contributionDay: contributionDay,
			monthlyCompanyContribution: resolvedMonthlyCompanyContribution,
			contributionPeriodicity: companyEppConfig?.contributionPeriodicity,
			isFreelance: usesReducedCap,
			grossAnnualSalary: userGrossAnnualSalary
		});
	}, [
		maxAportationsInfo,
		companyCommittedAnnual,
		externalPlanContributions,
		contributionDay,
		resolvedMonthlyCompanyContribution,
		companyEppConfig?.contributionPeriodicity,
		usesReducedCap,
		userGrossAnnualSalary
	]);

	// PERCENTAGE mode detection
	const isPercentageMode =
		(employeeContributionType as string) === EmployeeContributionTypeEnum.PERCENTAGE &&
		!!companyEppConfig?.seniorityBracket;

	// ceil a la rejilla: redondear un mínimo a la baja dejaría al partícipe por debajo del
	// mínimo pactado con su empresa.
	const percentageMin = useMemo(() => {
		if (!isPercentageMode) return 0;
		return ceilPercentageToGrid(
			companyEppConfig?.seniorityBracket?.minEmployeeContributionPercentage || 0
		);
	}, [isPercentageMode, companyEppConfig?.seniorityBracket]);

	// floor a la rejilla: el tope es un cálculo (tramo de la empresa o recomendación fiscal)
	// y arrastra decimales infinitos. Al pulsar "aportar el máximo" se persiste ESTE valor,
	// así que tiene que ser un múltiplo de 0,05 y hacia abajo — nunca por encima del techo.
	const percentageMax = useMemo(() => {
		if (!isPercentageMode || !companyEppConfig?.seniorityBracket) return 5;
		const minValue = ceilPercentageToGrid(
			companyEppConfig.seniorityBracket.minEmployeeContributionPercentage || 0
		);

		// For salaries > 60k with seniority tiers: max % = employer tier %
		if (userGrossAnnualSalary > 60000 && companyEppConfig.seniorityBracket.tiers) {
			const employmentStartDate = maxAportationsInfo?.user?.employmentStartDate;
			if (employmentStartDate) {
				const yearsOfService = calculateYearsOfService(employmentStartDate);
				const tier = companyEppConfig.seniorityBracket.tiers.find(
					(t) =>
						yearsOfService >= t.minYears && (t.maxYears === null || yearsOfService < t.maxYears)
				);
				if (tier?.employerContributionPercentage) {
					return Math.max(minValue, floorPercentageToGrid(tier.employerContributionPercentage));
				}
			}
		}

		// Fallback: calculate from recommended € amount
		if (userGrossAnnualSalary > 0) {
			const recommendedMonthly =
				maxAportationsInfo?.recommendedConfig?.participantContributionPerPeriod || 0;
			const calculatedMax = ((recommendedMonthly * 12) / userGrossAnnualSalary) * 100;
			return Math.max(minValue, floorPercentageToGrid(calculatedMax));
		}

		return Math.max(minValue, 5);
	}, [
		isPercentageMode,
		companyEppConfig?.seniorityBracket,
		userGrossAnnualSalary,
		maxAportationsInfo
	]);

	// Convert percentage to monthly € (helper compartido con el simulador del hero)
	const percentageToMonthlyEur = useCallback(
		(pct: number) => percentageToMonthlyEurUtil(pct, userGrossAnnualSalary),
		[userGrossAnnualSalary]
	);

	// MATCHING mode: the employer matches the live slider value (capped per tier),
	// so the "company contributes" card updates in real time
	const isMatchingMode = !!companyEppConfig?.matchingBracket;
	const matchingTier = useMemo(
		() =>
			companyEppConfig?.matchingBracket
				? resolveMatchingTier(companyEppConfig.matchingBracket, employeeCategory)
				: null,
		[companyEppConfig?.matchingBracket, employeeCategory]
	);
	const liveMonthlyCompanyContribution = useMemo(() => {
		if (!companyEppConfig?.matchingBracket) return resolvedMonthlyCompanyContribution;
		const liveEmployeeMonthly = isPercentageMode
			? percentageToMonthlyEur(percentageValue)
			: Number(monthlyAmount || 0);
		return resolveMatchingContribution({
			matchingBracket: companyEppConfig.matchingBracket,
			employeeCategory,
			employeeMonthlyContribution: liveEmployeeMonthly
		});
	}, [
		companyEppConfig?.matchingBracket,
		employeeCategory,
		isPercentageMode,
		percentageToMonthlyEur,
		percentageValue,
		monthlyAmount,
		resolvedMonthlyCompanyContribution
	]);

	// Initialize percentage value when data loads
	useEffect(() => {
		if (!isPercentageMode || !isOpen) return;
		// El % guardado puede venir de antes de la rejilla (con decimales de sobra): se
		// muestra ya alineado, que es como se guardará en cuanto se toque.
		const currentPct =
			typeof contributionFixedAmount === 'number' && contributionFixedAmount > 0
				? snapPercentageToGrid(contributionFixedAmount)
				: percentageMin;
		setPercentageValue(Math.max(currentPct, percentageMin));
	}, [isOpen, isPercentageMode]); // eslint-disable-line react-hooks/exhaustive-deps

	const totalAnnualPlan = companyAnnualContribution + alreadyContributedEmployee;
	const employmentPlanLimit = isIndividual
		? 0
		: usesReducedCap
			? CONTRIBUTION_LIMITS.FREELANCE_PLAN
			: CONTRIBUTION_LIMITS.EMPLOYEE_PLAN;
	const combinedLimit = isIndividual
		? CONTRIBUTION_LIMITS.GLOBAL_INDIVIDUAL
		: usesReducedCap
			? CONTRIBUTION_LIMITS.GLOBAL_FREELANCE
			: CONTRIBUTION_LIMITS.GLOBAL_EMPLOYEE;
	const planProgress =
		employmentPlanLimit > 0 ? Math.min((totalAnnualPlan / employmentPlanLimit) * 100, 100) : 0;
	const totalGlobal = totalAnnualPlan + externalPlanContributions;
	const globalProgress = Math.min((totalGlobal / combinedLimit) * 100, 100);
	const individualSlot =
		breakdown?.step3_individualSlot ?? Math.max(0, 1500 - externalPlanContributions);

	// Handlers
	const handleSetMax = async () => {
		if (isPercentageMode) {
			setPercentageValue(percentageMax);
			handleChangeFixedContribution(percentageMax);
		} else {
			setMonthlyAmount(maxMonthlyEmployee);
			handleChangeFixedContribution(maxMonthlyEmployee);
		}
		setContributeAlwaysMax(false);
	};

	const handleSliderChange = (values: number[]) => {
		const value = values[0];
		setMonthlyAmount(value);
		setContributeAlwaysMax(false);

		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}
		debounceTimeoutRef.current = setTimeout(() => {
			handleChangeFixedContribution(value);
		}, 300);
	};

	const handleSave = async () => {
		// Usamos el importe capturado al abrir el modal (no el actual del prop):
		// el slider y los botones quick-action ya han actualizado el prop del
		// padre vía handleChangeFixedContribution, así que leerlo ahora daría
		// previousAmount === monthlyAmount.
		// Con el preset, lo previo es lo que aportaba al abrir (puede ser 0): el ref de arriba no
		// guarda ceros y, con el preset ya aplicado, podría haber capturado el importe nuevo.
		const fromPreset = presetBaseAmount !== null;
		const previousAmount = fromPreset ? presetBaseAmount : initialContributionAmountRef.current;
		const adjustmentMethod = !fromPreset
			? undefined
			: monthlyAmount === presetInitialAmountRef.current
				? CONTRIBUTION_ADJUSTMENT_METHODS.PRESET_DELTA
				: CONTRIBUTION_ADJUSTMENT_METHODS.MANUAL_SLIDER;
		setIsSaving(true);
		try {
			const saved = await handleSaveChangesEditFixedContribution();
			if (saved === false) return;
			if (shouldFireContributionUpdated(previousAmount, monthlyAmount, adjustmentMethod)) {
				analytics.contributionUpdated({
					previousAmount: previousAmount as number,
					newAmount: monthlyAmount,
					...(adjustmentMethod
						? { adjustmentMethod, mode: isIndividual ? 'INDIVIDUAL' : 'EMPLOYMENT' }
						: {})
				});
			}
		} finally {
			setIsSaving(false);
		}
	};

	const shouldBlockModification =
		companyEppConfig !== undefined && daysUntilContribution <= 4 && daysUntilContribution >= 0;

	const isContributionTooLow =
		companyEppConfig?.employeeContributionRequired && monthlyAmount < minMonthlyEmployee;

	// Textos del correo solo si de verdad se puede editar (no en los 4 días de nómina cerrada).
	const showPresetTexts = presetBaseAmount !== null && !shouldBlockModification;

	return (
		<>
			<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
				<DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle className="text-xl font-bold">
							{!showPresetTexts
								? translate('user.myContributions.modalEditMyContribution')
								: translate(
										(presetBaseAmount as number) > 0
											? 'contributionPreset.raiseTitle'
											: 'contributionPreset.activateTitle'
									).replace(
										'{{amount}}',
										// Lo que sube de verdad: con tope en el máximo puede ser menos.
										fmtNum(
											(presetInitialAmountRef.current as number) - (presetBaseAmount as number),
											0
										)
									)}
						</DialogTitle>
						<DialogDescription className="text-muted-foreground">
							{!showPresetTexts
								? translate('user.myContributions.modalDescription')
								: translate('contributionPreset.activateMessage').replace(
										'{{amount}}',
										fmtNum(monthlyAmount, 0)
									)}
						</DialogDescription>
					</DialogHeader>

					{isLoading ? (
						<div className="py-12 flex flex-col items-center justify-center gap-4">
							<Loader2 className="w-8 h-8 animate-spin text-primary" />
							<p className="text-sm text-muted-foreground">{translate('common.loading')}...</p>
						</div>
					) : shouldBlockModification ? (
						<div>
							<div className="py-6 text-center">
								<AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
								<p className="text-sm text-gray-700">
									{translate('user.myContributions.modalPayrollClosedMessage')}
									<strong> {contributionDay + 1} </strong>
									{translate('user.myContributions.modalPayrollClosedMessage2')}
								</p>
							</div>
							<div className="flex justify-center">
								<Button variant="outline" onClick={onClose} className="rounded-full">
									{translate('user.myContributions.modalCloseAction')}
								</Button>
							</div>
						</div>
					) : (
						<div className="space-y-6 pt-2">
							{/* ── Amount selector ── */}
							<Card className="border-2 border-primary/20 bg-primary/5">
								<CardContent className="p-6 space-y-5">
									{/* Quick max button */}
									<div className="flex justify-center">
										<Button
											variant="outline"
											onClick={handleSetMax}
											className="border-primary text-primary hover:bg-primary/10 rounded-full gap-2"
										>
											<Sparkles className="w-4 h-4" />
											{translate('user.myContributions.wantToContributeMax')}
										</Button>
									</div>

									{/* Big amount display */}
									<div className="text-center">
										{isPercentageMode ? (
											<>
												<p className="text-5xl font-bold text-foreground tracking-tight">
													{fmtNum(percentageValue, 2)}%
													<span className="text-2xl text-muted-foreground ml-1">
														{translate('user.myContributions.ofSalary') || 'de tu salario'}
													</span>
												</p>
												<p className="text-lg text-primary font-medium mt-1">
													= {fmtEur(percentageToMonthlyEur(percentageValue))}/mes
												</p>
											</>
										) : (
											<p className="text-5xl font-bold text-foreground tracking-tight">
												{fmtNum(monthlyAmount, 0)} €
												<span className="text-2xl text-muted-foreground ml-1">/mes</span>
											</p>
										)}
									</div>

									{/* Slider */}
									<div className="space-y-2 px-2">
										{isPercentageMode ? (
											<>
												<Slider
													value={[percentageValue]}
													onValueChange={(values) => {
														// A la rejilla antes de nada: este valor es el que se muestra Y el
														// que se persiste, así que tiene que caber en 2 decimales.
														const pct = snapPercentageToGrid(values[0]);
														setPercentageValue(pct);
														setContributeAlwaysMax(false);
														if (debounceTimeoutRef.current) {
															clearTimeout(debounceTimeoutRef.current);
														}
														debounceTimeoutRef.current = setTimeout(() => {
															handleChangeFixedContribution(pct);
														}, 300);
													}}
													max={percentageMax}
													min={percentageMin}
													step={CONTRIBUTION_PERCENTAGE_STEP}
													className="**:[[role=slider]]:bg-primary **:[[role=slider]]:border-primary"
												/>
												<div className="flex justify-between text-xs text-muted-foreground">
													<span>Min: {fmtNum(percentageMin, 2)}%</span>
													<span className="font-medium text-primary">
														{translate('user.myContributions.sliderMax')}:{' '}
														{fmtNum(percentageMax, 2)}%
													</span>
												</div>
											</>
										) : (
											<>
												<Slider
													value={[monthlyAmount]}
													onValueChange={handleSliderChange}
													max={maxMonthlyEmployee}
													min={sliderMinMonthly}
													step={1}
													className="**:[[role=slider]]:bg-primary **:[[role=slider]]:border-primary"
												/>
												<div className="flex justify-between text-xs text-muted-foreground">
													<span>
														{sliderMinMonthly > 0 ? `Min: ${fmtEur(sliderMinMonthly)}` : '0 €'}
													</span>
													<span className="font-medium text-primary">
														{translate('user.myContributions.sliderMax')}:{' '}
														{fmtEur(maxMonthlyEmployee)}
													</span>
												</div>
											</>
										)}
									</div>

									{/* Always max checkbox */}
									<div className="flex items-center justify-center gap-2">
										<Checkbox
											id="always-max"
											checked={contributeAlwaysMax}
											onCheckedChange={(checked) => {
												if (checked) {
													setShouldEnableAlwaysMax(true);
													setShowPensionPlanFlow(true);
												} else {
													setContributeAlwaysMax(false);
												}
											}}
										/>
										{/* 🔴 El tooltip va FUERA del `<label>`, no dentro.
										    Dentro, sus píxeles formaban parte del área que marca la casilla:
										    pulsar ahí abría el tooltip en lugar de marcarla, y el nombre
										    accesible de la casilla acababa incluyendo el texto del botón de
										    ayuda. El área táctil de 44 px que el `responsive-tooltip` del
										    registry trae desde el 26/09/26 agranda ese solape. */}
										<span className="flex items-center gap-1">
											<label htmlFor="always-max" className="text-sm cursor-pointer">
												{translate('user.myContributions.checkboxContributeAlwaysMax')}
											</label>
											<ResponsiveTooltip
												content={translate('user.myContributions.checkboxInfoText')}
												side="top"
											/>
										</span>
									</div>

									{/* Recommendation nudge */}
									{(isPercentageMode
										? percentageValue < percentageMax && percentageMax > 0
										: monthlyAmount < maxMonthlyEmployee && maxMonthlyEmployee > 0) && (
										<div className="flex items-start gap-2 p-3 rounded-lg bg-secondary/10 border border-secondary/20">
											<TrendingUp className="w-4 h-4 text-secondary mt-0.5 shrink-0" />
											<p className="text-xs text-muted-foreground">
												{translate('user.myContributions.recommendationNudge').replace(
													'{{amount}}',
													isPercentageMode
														? `${fmtNum(percentageMax, 2)}%`
														: fmtEur(maxMonthlyEmployee)
												)}{' '}
												<button
													onClick={handleSetMax}
													className="font-semibold text-secondary underline underline-offset-2 hover:text-secondary/80"
												>
													{translate('user.myContributions.setMaxButton')}
												</button>
											</p>
										</div>
									)}
								</CardContent>
							</Card>

							{/* ── What you'll receive ── */}
							<div className="grid grid-cols-2 gap-3">
								<Card className="border-none shadow-xs">
									<CardContent className="p-4 flex items-center gap-3">
										<div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
											<Building2 className="w-4 h-4 text-primary" />
										</div>
										<div>
											<p className="text-xs text-muted-foreground">
												{translate('user.myContributions.companyContributes')}
											</p>
											<p className="text-lg font-bold">
												{fmtEur(
													isMatchingMode
														? liveMonthlyCompanyContribution ?? 0
														: resolvedMonthlyCompanyContribution ?? companyAnnualContribution
												)}
												<span className="text-xs font-normal text-muted-foreground">/mes</span>
											</p>
											{isMatchingMode && matchingTier?.maxAmount !== undefined && (
												<p className="text-xs text-muted-foreground">
													{translate('user.myContributions.matchingHint')}{' '}
													{fmtEur(matchingTier.maxAmount)}/mes
												</p>
											)}
										</div>
									</CardContent>
								</Card>
								<Card className="border-none shadow-xs">
									<CardContent className="p-4 flex items-center gap-3">
										<div className="w-9 h-9 rounded-lg bg-secondary/10 flex items-center justify-center shrink-0">
											<User className="w-4 h-4 text-secondary" />
										</div>
										<div>
											<p className="text-xs text-muted-foreground">
												{translate('user.myContributions.youWillContribute')}
											</p>
											<p className="text-lg font-bold text-secondary">
												{isPercentageMode ? (
													<>{fmtNum(percentageValue, 2)}%</>
												) : (
													<>{fmtEur(monthlyAmount)}</>
												)}
												<span className="text-xs font-normal text-muted-foreground">
													{isPercentageMode
														? ` (${fmtEur(percentageToMonthlyEur(percentageValue))}/mes)`
														: '/mes'}
												</span>
											</p>
										</div>
									</CardContent>
								</Card>
							</div>

							{/* ── Annual limits ── */}
							<Card className="border-none shadow-xs">
								<CardContent className="p-4 space-y-4">
									<div className="flex items-center justify-between">
										<h4 className="text-sm font-semibold">
											{translate('user.myContributions.annualLimitEmploymentPlan')}
										</h4>
										<Badge variant="outline" className="text-xs border-primary/30 text-primary">
											{fmtNum(totalAnnualPlan, 0)} € / {fmtNum(employmentPlanLimit, 0)} €
										</Badge>
									</div>
									<Progress value={planProgress} className="h-2" />

									<div className="flex items-center justify-between mt-3">
										<h4 className="text-sm font-semibold">
											{translate('user.myContributions.globalLimit')}
										</h4>
										<Badge variant="outline" className="text-xs border-muted-foreground/30">
											{fmtNum(totalGlobal, 0)} € / {fmtNum(combinedLimit, 0)} €
										</Badge>
									</div>
									<Progress value={globalProgress} className="h-2" />

									<p className="text-xs text-muted-foreground">
										{externalPlanContributions > 0 ? (
											<>
												{translate('user.myContributions.externalPlanNote')
													.replace('{{external}}', fmtNum(externalPlanContributions, 0))
													.replace('{{remaining}}', fmtNum(individualSlot, 0))}
											</>
										) : (
											<>
												{translate('user.myContributions.noExternalPlanNote').replace(
													'{{available}}',
													fmtNum(individualSlot, 0)
												)}
											</>
										)}
									</p>
								</CardContent>
							</Card>

							{/* ── How your limit is calculated ── */}
							<details className="group">
								<summary className="flex items-center gap-2 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
									<Info className="w-4 h-4" />
									{translate('user.myContributions.howLimitCalculated')}
									<span className="ml-auto text-xs group-open:hidden">
										{translate('user.myContributions.viewDetails')}
									</span>
								</summary>
								<div className="mt-3 space-y-3">
									{/* Your case summary */}
									<Card className="border-2 border-primary/30 bg-primary/5">
										<CardContent className="p-4">
											<div className="flex items-center gap-2 mb-3">
												<CheckCircle2 className="w-4 h-4 text-primary" />
												<p className="text-sm font-semibold text-primary">
													{translate('user.myContributions.yourCase')}
												</p>
											</div>

											{breakdown && (
												<div className="space-y-2.5">
													<div className="flex items-center justify-between text-sm">
														<span className="text-muted-foreground">
															① {translate('user.myContributions.stepCompanyAnnual')}
														</span>
														<span className="font-bold">
															{fmtEur(breakdown.step1_companyAnnual)}
														</span>
													</div>

													<div className="flex items-center justify-between text-sm">
														<span className="text-muted-foreground">
															② {translate('user.myContributions.stepBracketApplied')} (
															{breakdown.step2_bracketMultiplier})
														</span>
														<span className="font-bold text-primary">
															→ {fmtEur(breakdown.step2_bracketLimit)}
														</span>
													</div>

													<div className="flex items-center justify-between text-sm">
														<span className="text-muted-foreground">
															③ {translate('user.myContributions.stepIndividualSlot')}
														</span>
														<span className="font-bold">
															+ {fmtEur(breakdown.step3_individualSlot)}
														</span>
													</div>

													<div className="flex items-center justify-between text-sm">
														<span className="text-muted-foreground">
															④ {translate('user.myContributions.stepMaxCombined')}
														</span>
														<span className="font-bold">{fmtEur(breakdown.step4_maxCombined)}</span>
													</div>

													<div className="border-t border-primary/20 my-1" />

													<div className="flex items-center justify-between text-sm">
														<span className="text-muted-foreground">
															⑤ {translate('user.myContributions.stepAlreadyContributed')}
														</span>
														<span className="font-semibold">
															{breakdown.step5_alreadyContributed > 0 ? '− ' : ''}
															{fmtEur(breakdown.step5_alreadyContributed)}
														</span>
													</div>

													<div className="flex items-center justify-between text-sm">
														<span className="text-muted-foreground">
															⑥ {translate('user.myContributions.stepRemainingBudget')}
														</span>
														<span className="font-semibold">
															{fmtEur(breakdown.step6_remainingBudget)}
														</span>
													</div>

													<div className="flex items-center justify-between text-sm">
														<span className="text-muted-foreground">
															⑦ ÷ {breakdown.step7_remainingMonths}{' '}
															{translate('user.myContributions.stepMonthsRemaining')}
														</span>
														<span className="font-bold text-primary text-base">
															= {fmtEur(breakdown.recommendedMonthly)}/mes
														</span>
													</div>
												</div>
											)}

											<p className="text-xs text-muted-foreground mt-3 leading-relaxed">
												{translate('user.myContributions.limitExplanation')}
											</p>
										</CardContent>
									</Card>

									{/* Legal brackets */}
									<div className="space-y-2">
										<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
											{translate('user.myContributions.legalBrackets')}
										</p>
										{[
											{
												range: translate('user.myContributions.bracket1Range'),
												rule: translate('user.myContributions.bracket1Rule'),
												active: breakdown?.activeBracket === 1 && userGrossAnnualSalary <= 60000
											},
											{
												range: translate('user.myContributions.bracket2Range'),
												rule: translate('user.myContributions.bracket2Rule'),
												active: breakdown?.activeBracket === 2 && userGrossAnnualSalary <= 60000
											},
											{
												range: translate('user.myContributions.bracket3Range'),
												rule: translate('user.myContributions.bracket3Rule'),
												active: breakdown?.activeBracket === 3 && userGrossAnnualSalary <= 60000
											}
										].map((bracket, i) => (
											<div
												key={i}
												className={`relative rounded-xl p-3 border-2 transition-all ${
													bracket.active
														? 'border-primary bg-primary/5 shadow-xs'
														: 'border-border bg-card opacity-60'
												}`}
											>
												{bracket.active && (
													<Badge className="absolute -top-2.5 right-3 bg-primary text-primary-foreground text-[10px] px-2 py-0.5">
														{translate('user.myContributions.yourBracket')}
													</Badge>
												)}
												<div className="flex items-start gap-3">
													<div
														className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 ${
															bracket.active
																? 'bg-primary text-primary-foreground'
																: 'bg-muted text-muted-foreground'
														}`}
													>
														{i + 1}
													</div>
													<div className="space-y-0.5 min-w-0">
														<p
															className={`text-sm font-semibold ${
																bracket.active ? 'text-foreground' : 'text-muted-foreground'
															}`}
														>
															{bracket.range}
														</p>
														<p
															className={`text-sm ${
																bracket.active ? 'text-foreground' : 'text-muted-foreground'
															}`}
														>
															{bracket.rule}
														</p>
													</div>
												</div>
											</div>
										))}
									</div>

									{/* Salary warning */}
									{userGrossAnnualSalary > 60000 && (
										<div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
											<AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
											<p className="text-xs text-muted-foreground">
												{translate('user.myContributions.highSalaryWarning')}
											</p>
										</div>
									)}

									{/* Global cap reminder */}
									<p className="text-xs text-muted-foreground px-1">
										{translate('user.myContributions.globalCapReminder')}
									</p>
								</div>
							</details>

							{/* ── Minimum contribution warning ── */}
							{isContributionTooLow && (
								<div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
									<AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
									<p className="text-xs text-muted-foreground">
										{(employeeContributionType as string) ===
										EmployeeContributionTypeEnum.PERCENTAGE
											? translate('user.myContributions.minimumContributionWarning')
													.replace('{{min}}', fmtNum(minMonthlyEmployee))
													.replace(
														'{{percentage}}',
														String(companyEppConfig?.employeeContributionMinimum || 0)
													)
											: translate('user.myContributions.minimumContributionWarningFixed').replace(
													'{{min}}',
													fmtNum(minMonthlyEmployee)
												)}
									</p>
								</div>
							)}

							{/* ── Action buttons ── */}
							<div className="flex gap-3 pt-2">
								<Button variant="outline" onClick={onClose} className="flex-1 rounded-full">
									{translate('common.cancel')}
								</Button>
								<Button
									onClick={handleSave}
									disabled={isSaving || !!isContributionTooLow}
									className="flex-1 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
								>
									{isSaving ? (
										<Loader2 className="w-4 h-4 animate-spin" />
									) : (
										<CheckCircle2 className="w-4 h-4" />
									)}
									{translate('user.myContributions.confirmContribution')}
								</Button>
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>

			{/* Individual Pension Plan Flow */}
			{showPensionPlanFlow && (
				<IndividualPensionPlanFlowV2
					isOpen={showPensionPlanFlow}
					onClose={() => {
						setShowPensionPlanFlow(false);
						setShouldEnableAlwaysMax(false);
					}}
					onComplete={async () => {
						if (shouldEnableAlwaysMax) {
							setContributeAlwaysMax(true);
						}
						const freshData = await fetchMaxAportationsInfo();
						if (freshData.employeeData) {
							const newMax = freshData.employeeData.maxUserContributionEndOfNextPeriod || 0;
							setMonthlyAmount(newMax);
							handleChangeFixedContribution(newMax);
						}
						setShouldEnableAlwaysMax(false);
					}}
					token={token}
				/>
			)}
		</>
	);
};

export default EditContributionModalV2;
